import { formatDistanceToNow } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import type { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerOrderListItem } from "@/lib/api/types";
import type { QueueBucket } from "./order-status";
import { ORDERS_CURRENCY_CODE } from "./order-list.constants";

export function hasArabicChars(value: string): boolean {
  return /[\u0600-\u06FF]/.test(value);
}

export function formatRelativeTime(locale: string, isoDate: string): string {
  const language = locale === "ar" ? ar : enUS;
  return formatDistanceToNow(new Date(isoDate), {
    addSuffix: true,
    locale: language,
  });
}

export function formatAbsoluteDate(locale: string, isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(locale === "ar" ? "ar-EG" : "en-US");
}

export function formatAmountValue(locale: string, amount: number): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US", {
    style: "currency",
    currency: ORDERS_CURRENCY_CODE,
    // Use Western (Latin) digits even in Arabic so amounts match the rest of
    // the app (order codes, dates, the approved-amount input) instead of
    // rendering as Arabic-Indic numerals (٥٤٨).
    numberingSystem: "latn",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function resolveProductItemName(
  locale: string,
  item: SellerOrderListItem["items"][number],
): string {
  if (locale === "en") {
    return (
      item.resolvedTitleEn ??
      item.productNameEn ??
      item.resolvedTitleAr ??
      item.productNameAr
    );
  }
  return item.resolvedTitleAr ?? item.productNameAr;
}

export function getPrimaryActionLabel(
  status: SellerOrderListItem["status"],
  queueBucket: QueueBucket,
  t: ReturnType<typeof useTranslations>,
): string {
  if (queueBucket !== "needsAction") {
    return t(keys.orders.openOrderAction);
  }

  if (status === "pending_payment_verification") {
    return t(keys.orders.reviewAction);
  }
  if (status === "pending_buyer_verification") {
    return t(keys.orders.verifyAction);
  }
  return t(keys.orders.followUpAction);
}


export function getAddressDisplayMeta(locale: string, address: string) {
  const isRtl = locale === "ar";
  const isArabicAddress = hasArabicChars(address);
  const isForeignAddress = (isRtl && !isArabicAddress) || (!isRtl && isArabicAddress);
  const dir: "auto" | "rtl" | "ltr" = isForeignAddress
    ? isArabicAddress
      ? "rtl"
      : "ltr"
    : "auto";

  const hintKey = isForeignAddress
    ? isArabicAddress
      ? keys.orders.addressArabicHint
      : keys.orders.addressEnglishHint
    : null;

  const textClassName = isForeignAddress
    ? dir === "rtl"
      ? "truncate text-right"
      : "truncate text-left"
    : "line-clamp-1";

  return {
    dir,
    hintKey,
    textClassName,
  };
}
